import { API_ENDPOINTS } from '../../constants/api';
import { httpClient } from '../api/http-client';
import type { BulkUploadRequest, BulkUploadResult, BulkMembershipItem } from '../../types';

// Interfaz para los datos crudos del Excel
interface BulkDatosComplementariosItemRaw {
  numeroDocumento: string;
  nivelEstudios?: string;
  profesionOcupacion?: string;
  tiempoAsistenciaIglesia?: string;
  oracionFe?: string;
  esBautizado?: string;
  bautismoAno?: number;
  bautismoIglesia?: string;
  bautismoMinistro?: string;
  estudiosCursos?: string;
  tipoPersonalidad?: string;
  habilidades?: string;
  donesEspirituales?: string;
  ministeriosParticipacion?: string;
  ministeriosDeseados?: string;
  fechaEncuesta?: string;
  // Permitir acceso con índices dinámicos para columnas no mapeadas
  [key: string]: string | number | undefined;
}

// Interfaz para los datos transformados que espera el backend
interface BulkDatosComplementariosItem {
  numeroDocumento: string;
  nivelEstudios?: string;
  profesion?: string;
  tiempoAsistencia?: string;
  oracionFe: boolean;
  esBautizado: boolean;
  añoBautismo?: number;
  iglesiaBautismo?: string;
  ministroBautismo?: string;
  estudiosRealizados: string[];
  tipoPersonalidad?: string;
  habilidades: string[];
  dones: string[];
  perteneceMinisterio: boolean;
  ministeriosActuales: string[];
  ministerioDeseado?: string;
  fechaEncuesta: string;
}

export class BulkUploadService {
  async uploadMemberships(memberships: BulkMembershipItem[]): Promise<BulkUploadResult> {
    const payload: BulkUploadRequest = {
      memberships
    };

    // Usar un timeout mayor para la carga masiva (120 segundos)
    const response = await httpClient.post<BulkUploadResult>(
      API_ENDPOINTS.memberships.bulkUpload,
      payload,
      {
        timeout: 120000 // 2 minutos
      }
    );
    
    // El backend puede devolver los datos en response.data.data o directamente en response.data
    const result = (response.data as any).data || response.data;
    return result;
  }

  async uploadDatosComplementarios(
    datosComplementariosRaw: BulkDatosComplementariosItemRaw[],
    skippedInfo?: { count: number; names: string[] }
  ): Promise<BulkUploadResult> {
    // Transformar los datos del Excel al formato que espera el backend
    const datosComplementarios: BulkDatosComplementariosItem[] = datosComplementariosRaw.map(item => {
      // Parsear año de bautismo (puede venir como número o string del Excel)
      const añoBautismoRaw = item.bautismoAno ?? (item as any)['añoBautismo'] ?? (item as any)['Año aproximado de su bautismo'];
      
      return {
        numeroDocumento: item.numeroDocumento,
        nivelEstudios: this.normalizeNivelEstudios(item.nivelEstudios),
        profesion: item.profesionOcupacion,
        tiempoAsistencia: this.normalizeTiempoAsistencia(item.tiempoAsistenciaIglesia),
        oracionFe: this.parseBoolean(item.oracionFe, false),
        esBautizado: this.parseBoolean(item.esBautizado, false),
        añoBautismo: this.normalizeAñoBautismo(añoBautismoRaw),
        iglesiaBautismo: item.bautismoIglesia,
        ministroBautismo: item.bautismoMinistro,
        estudiosRealizados: this.parseArray(item.estudiosCursos),
        tipoPersonalidad: this.normalizeTipoPersonalidad(item.tipoPersonalidad),
        habilidades: this.parseArray(item.habilidades),
        dones: this.parseArray(item.donesEspirituales),
        perteneceMinisterio: this.parseBoolean(item.ministeriosParticipacion, false),
        ministeriosActuales: this.parseArray(item.ministeriosParticipacion),
        ministerioDeseado: item.ministeriosDeseados,
        fechaEncuesta: this.formatFechaEncuesta(item.fechaEncuesta),
      };
    });

    const payload = {
      datosComplementarios
    };

    // Usar un timeout mayor para la carga masiva (120 segundos)
    const response = await httpClient.post<any>(
      API_ENDPOINTS.memberships.bulkUploadDatosComplementarios,
      payload,
      {
        timeout: 120000 // 2 minutos
      }
    );
    
    // Transformar la respuesta del backend al formato esperado por el frontend
    // El backend devuelve: { data: { success, message, data: { processed, successful, failed, errors } } }
    const backendData = response.data?.data?.data || response.data?.data || response.data;
    
    const result: BulkUploadResult = {
      success: backendData.successful || 0,
      total: backendData.processed || 0,
      errors: (backendData.errors || []).map((err: any, index: number) => ({
        row: index + 1,
        message: err.error || 'Error desconocido',
        data: err.data || err,
        type: 'validation'
      })),
      created: (backendData.successful_records || []).map((r: any) => r.numeroDocumento)
    };
    
    return result;
  }

  // Helper methods para transformación de datos
  private parseBoolean(value: string | undefined, defaultValue?: boolean): boolean {
    if (!value) return defaultValue ?? false;
    const normalized = value.toLowerCase().trim();
    return normalized === 'sí' || normalized === 'si' || normalized === 'yes' || normalized === 'true' || normalized === '1';
  }

  private parseArray(value: string | undefined): string[] {
    if (!value) return [];
    // Separar por comas y limpiar espacios
    return value.split(/[,;]/).map(item => item.trim()).filter(item => item.length > 0);
  }

  private normalizeNivelEstudios(value: string | undefined): string | undefined {
    if (!value) return undefined;
    const normalized = value.toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Quitar acentos
    
    if (normalized.includes('primaria')) return 'primaria';
    if (normalized.includes('secundaria')) return 'secundaria';
    if (normalized.includes('tecnico') || normalized.includes('tecnica')) return 'tecnico';
    if (normalized.includes('universitario') || normalized.includes('universidad') || normalized.includes('pregrado') || normalized.includes('profesional')) return 'universitario';
    if (normalized.includes('posgrado') || normalized.includes('maestria') || normalized.includes('doctorado') || normalized.includes('especializacion')) return 'posgrado';
    return undefined; // Retornar undefined si no coincide en lugar del valor crudo
  }

  private normalizeTiempoAsistencia(value: string | undefined): string | undefined {
    if (!value) return undefined;
    const normalized = value.toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Quitar acentos
    
    if (normalized.includes('menos') && (normalized.includes('1') || normalized.includes('ano') || normalized.includes('año'))) return 'menos_1_año';
    if ((normalized.includes('1') && normalized.includes('3')) || normalized.includes('1-3') || normalized.includes('1 a 3')) return '1_3_años';
    if (normalized.includes('mas') && (normalized.includes('3') || normalized.includes('tres'))) return 'mas_3_años';
    return undefined;
  }

  private normalizeTipoPersonalidad(value: string | undefined): string | undefined {
    if (!value) return undefined;
    const normalized = value.toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Quitar acentos
    
    // Manejar errores comunes de escritura
    if (normalized.includes('extrovertido') || normalized.includes('extroberto') || normalized.includes('extrovetido')) return 'extrovertido';
    if (normalized.includes('introvertido') || normalized.includes('introberto') || normalized.includes('introvetido')) return 'introvertido';
    if (normalized.includes('cooperativo') || normalized.includes('cooprativo') || normalized.includes('coprativo') || normalized.includes('cooperario')) return 'cooperativo';
    if (normalized.includes('intuitivo') || normalized.includes('intuitibo') || normalized.includes('intitivo')) return 'intuitivo';
    return undefined;
  }

  private normalizeAñoBautismo(value: string | number | undefined): number | undefined {
    if (!value) return undefined;
    const numValue = typeof value === 'string' ? Number(value) : value;
    if (isNaN(numValue)) return undefined;
    
    // Si es un año de 2 dígitos (10-99), asumir que es 20xx
    if (numValue >= 10 && numValue <= 99) {
      return 2000 + numValue;
    }
    // Si es menor a 1900 y mayor a 100, ignorarlo (posible error)
    if (numValue < 1900) return undefined;
    // Si es un año válido
    if (numValue <= new Date().getFullYear()) return numValue;
    
    return undefined;
  }

  /**
   * Procesa un archivo Excel y convierte los datos al formato requerido
   */
  async processExcelFile(file: File, type: 'basicos' | 'complementarios' = 'basicos'): Promise<BulkMembershipItem[] | BulkDatosComplementariosItem[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            throw new Error('No se pudo leer el archivo');
          }

          // Importar la librería XLSX dinámicamente
          const XLSX = await import('xlsx');
          
          // Leer el archivo Excel
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convertir a JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          // Procesar los datos según el tipo
          const processedData = type === 'basicos' 
            ? this.processExcelData(jsonData) 
            : this.processExcelDataComplementarios(jsonData);
          resolve(processedData);
        } catch (error) {
          reject(new Error(`Error al procesar el archivo Excel: ${error instanceof Error ? error.message : 'Error desconocido'}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };

      reader.readAsBinaryString(file);
    });
  }

  /**
   * Procesa los datos del Excel y los convierte al formato BulkMembershipItem
   */
  private processExcelData(data: any[][]): BulkMembershipItem[] {
    if (data.length < 2) {
      throw new Error('El archivo debe tener al menos un header y una fila de datos');
    }

    // Obtener headers (primera fila)
    const headers = data[0] as string[];
    
    // Mapear headers a índices (normalizar nombres de headers)
    const headerMap = new Map<string, number>();
    headers.forEach((header, index) => {
      if (header) {
        // Normalizar el header: quitar espacios, acentos, y convertir a lowercase
        const normalizedHeader = header.toString().trim().toLowerCase()
          .replace(/\s+/g, '')
          .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u')
          .replace(/ñ/g, 'n');
        headerMap.set(normalizedHeader, index);
      }
    });

    // Validar headers requeridos
    const requiredHeaders = [
      'nombres', 'apellidos', 'fechanacimiento', 'genero', 'estadocivil',
      'telefono', 'email', 'zonabarrio', 'consentimientodatos', 'autorizacionimagen'
    ];

    const missingHeaders = requiredHeaders.filter(header => !headerMap.has(header));
    if (missingHeaders.length > 0) {
      throw new Error(`Headers faltantes: ${missingHeaders.join(', ')}`);
    }

    // Procesar filas de datos
    const memberships: BulkMembershipItem[] = [];
    
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0 || !row.some(cell => cell !== null && cell !== undefined && cell !== '')) {
        continue; // Saltar filas vacías
      }

      try {
        const membership: BulkMembershipItem = {
          nombres: this.getCellValue(row, headerMap, 'nombres'),
          apellidos: this.getCellValue(row, headerMap, 'apellidos'),
          fechaNacimiento: this.formatDate(this.getCellValue(row, headerMap, 'fechanacimiento')),
          genero: this.getCellValue(row, headerMap, 'genero') as 'Masculino' | 'Femenino',
          estadoCivil: this.getCellValue(row, headerMap, 'estadocivil'),
          telefono: this.cleanPhone(this.getCellValue(row, headerMap, 'telefono')),
          email: this.getCellValue(row, headerMap, 'email').toLowerCase().trim(),
          zonaBarrio: this.getCellValue(row, headerMap, 'zonabarrio'),
          consentimientoDatos: this.parseBoolean(this.getCellValue(row, headerMap, 'consentimientodatos')),
          autorizacionImagen: this.parseBoolean(this.getCellValue(row, headerMap, 'autorizacionimagen')),
          numeroDocumento: this.getOptionalCellValue(row, headerMap, 'numerodocumento'),
          tipoDocumento: this.getOptionalCellValue(row, headerMap, 'tipodocumento'),
          observacionesLegales: this.getOptionalCellValue(row, headerMap, 'observacioneslegales')
        };

        // Procesar marcaTemporal si está presente en el Excel
        const marcaTemporalValue = this.getOptionalCellValue(row, headerMap, 'marcatemporal');
        if (marcaTemporalValue) {
          try {
            membership.marcaTemporal = this.formatDateTime(marcaTemporalValue);
          } catch (error) {
            // No asignar marcaTemporal si hay error - el backend lo generará automáticamente
          }
        } else {
        }

        // Validar datos básicos
        this.validateMembership(membership);
        memberships.push(membership);
      } catch (error) {
        continue;
      }
    }

    if (memberships.length === 0) {
      throw new Error('No se encontraron datos válidos en el archivo');
    }

    return memberships;
  }

  private getCellValue(row: any[], headerMap: Map<string, number>, header: string): string {
    const index = headerMap.get(header);
    if (index === undefined) {
      throw new Error(`Header '${header}' no encontrado`);
    }
    
    const value = row[index];
    if (value === null || value === undefined || value === '') {
      throw new Error(`Campo '${header}' es requerido`);
    }
    
    return String(value).trim();
  }

  private getOptionalCellValue(row: any[], headerMap: Map<string, number>, header: string): string | undefined {
    const index = headerMap.get(header);
    if (index === undefined) {
      return undefined;
    }
    
    const value = row[index];
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    
    // Para marcaTemporal, solo excluir si es exactamente '0' o 0, 
    // pero permitir otros valores numéricos (números de serie de Excel)
    if (header === 'marcatemporal' && (value === '0' || value === 0)) {
      return undefined;
    }
    
    // Para otros campos opcionales, excluir '0' como antes
    if (header !== 'marcatemporal' && value === '0') {
      return undefined;
    }
    
    return String(value).trim();
  }

  private formatDateTime(dateTimeValue: string): string {
    
    const stringValue = String(dateTimeValue).trim();
    
    // Si es un número (formato Excel), convertirlo usando XLSX
    const numValue = Number(stringValue);
    if (!isNaN(numValue) && numValue > 1) {
      // Excel dates start from 1900-01-01, but JavaScript Date starts from 1970-01-01
      // Excel day 1 = 1900-01-01, but Excel incorrectly treats 1900 as a leap year
      // So we need to subtract 1 day and use the correct Excel epoch
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
      const jsDate = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
      
      const year = jsDate.getFullYear();
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const day = String(jsDate.getDate()).padStart(2, '0');
      const hours = String(jsDate.getHours()).padStart(2, '0');
      const minutes = String(jsDate.getMinutes()).padStart(2, '0');
      const seconds = String(jsDate.getSeconds()).padStart(2, '0');
      
      const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      return formattedDateTime;
    }
    
    // Formato con segundos: DD/MM/YYYY HH:mm:ss
    const dateTimeWithSecondsRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/;
    const matchWithSeconds = stringValue.match(dateTimeWithSecondsRegex);
    
    if (matchWithSeconds) {
      const [, day, month, year, hours, minutes, seconds] = matchWithSeconds;
      const formattedDateTime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
      return formattedDateTime;
    }
    
    // Formato sin segundos: DD/MM/YYYY HH:mm (agregar :00 para segundos)
    // Más flexible para manejar espacios variables
    const dateTimeWithoutSecondsRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})$/;
    const matchWithoutSeconds = stringValue.match(dateTimeWithoutSecondsRegex);
    
    if (matchWithoutSeconds) {
      const [, day, month, year, hours, minutes] = matchWithoutSeconds;
      const formattedDateTime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
      return formattedDateTime;
    }
    
    // Si ya está en formato ISO, devolverlo tal como está
    if (stringValue.includes('T') && stringValue.includes('-')) {
      return stringValue;
    }
    
    throw new Error(`Formato de fecha y hora inválido para marcaTemporal: ${dateTimeValue}. Use DD/MM/YYYY HH:mm o DD/MM/YYYY HH:mm:ss`);
  }

  private formatDate(dateValue: string): string {
    
    // Si es un número (formato Excel), convertirlo usando XLSX
    const numValue = Number(dateValue);
    if (!isNaN(numValue) && numValue > 1) {
      // Excel dates start from 1900-01-01, but JavaScript Date starts from 1970-01-01
      // Excel day 1 = 1900-01-01, but Excel incorrectly treats 1900 as a leap year
      // So we need to subtract 1 day and use the correct Excel epoch
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
      const jsDate = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
      
      const year = jsDate.getFullYear();
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const day = String(jsDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      return formattedDate;
    }

    // Si es string, intentar diferentes formatos
    const stringValue = String(dateValue).trim();
    const dateFormats = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // DD/MM/YYYY
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
    ];

    for (const format of dateFormats) {
      const match = stringValue.match(format);
      if (match) {
        if (format === dateFormats[0]) {
          // DD/MM/YYYY -> YYYY-MM-DD
          const [, day, month, year] = match;
          const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          return formattedDate;
        } else {
          // Ya está en formato YYYY-MM-DD
          return stringValue;
        }
      }
    }

    throw new Error(`Formato de fecha inválido: ${dateValue}. Use DD/MM/YYYY, YYYY-MM-DD o deje que Excel maneje el formato automáticamente`);
  }

  private cleanPhone(phone: string): string {
    // Remover espacios y caracteres no numéricos excepto +
    return phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
  }

  private validateMembership(membership: BulkMembershipItem): void {
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(membership.email)) {
      throw new Error(`Email inválido: ${membership.email}`);
    }

    // Validar género
    if (!['Masculino', 'Femenino'].includes(membership.genero)) {
      throw new Error(`Género debe ser 'Masculino' o 'Femenino', recibido: ${membership.genero}`);
    }

    // Validar estado civil según género
    const estadoCivilLower = membership.estadoCivil.toLowerCase().trim();
    if (membership.genero === 'Femenino') {
      const estadosValidosFemenino = ['soltera', 'casada', 'viuda', 'divorciada', 'unión libre'];
      if (!estadosValidosFemenino.includes(estadoCivilLower)) {
        throw new Error(`Para género femenino, el estado civil debe ser: soltera, casada, viuda, divorciada o unión libre. Recibido: ${membership.estadoCivil}`);
      }
    } else if (membership.genero === 'Masculino') {
      const estadosValidosMasculino = ['soltero', 'casado', 'viudo', 'divorciado', 'unión libre'];
      if (!estadosValidosMasculino.includes(estadoCivilLower)) {
        throw new Error(`Para género masculino, el estado civil debe ser: soltero, casado, viudo, divorciado o unión libre. Recibido: ${membership.estadoCivil}`);
      }
    }

    // Validar fecha de nacimiento
    const birthDate = new Date(membership.fechaNacimiento);
    if (isNaN(birthDate.getTime())) {
      throw new Error(`Fecha de nacimiento inválida: ${membership.fechaNacimiento}`);
    }

    // Validar que la fecha no sea futura (con margen de 1 día por diferencias de zona horaria)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (birthDate > tomorrow) {
      throw new Error(`La fecha de nacimiento no puede ser futura: ${membership.fechaNacimiento}`);
    }

    // Validar que la fecha no sea demasiado antigua (antes de 1900)
    if (birthDate.getFullYear() < 1900) {
      throw new Error(`La fecha de nacimiento es demasiado antigua: ${membership.fechaNacimiento}`);
    }
  }

  /**
   * Procesa los datos del Excel para datos complementarios
   */
  private processExcelDataComplementarios(data: any[][]): BulkDatosComplementariosItem[] {
    if (data.length < 2) {
      throw new Error('El archivo debe tener al menos un header y una fila de datos');
    }

    // Obtener headers (primera fila)
    const headers = data[0] as string[];
    
    // Mapear headers a índices (normalizar nombres de headers)
    const headerMap = new Map<string, number>();
    headers.forEach((header, index) => {
      if (header) {
        // Normalizar el header: quitar espacios, acentos, caracteres especiales y convertir a lowercase
        const normalizedHeader = header.toString().trim().toLowerCase()
          .replace(/\s+/g, '')
          .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u')
          .replace(/ñ/g, 'n')
          .replace(/[^a-z0-9]/g, ''); // Quitar caracteres especiales como @, ?, ¿, etc.
        headerMap.set(normalizedHeader, index);
      }
    });

    // Debug: mostrar headers encontrados
    console.log('Headers encontrados en el Excel:', Array.from(headerMap.keys()));

    // Mapeo de posibles variaciones de headers a nombres estándar
    // IMPORTANTE: Los headers se normalizan: lowercase, sin espacios, sin acentos, sin caracteres especiales
    const headerAliases: Record<string, string[]> = {
      'numerodocumento': ['numerodocumento', 'numerodedocumento', 'documento', 'cedula', 'cc'],
      'nivelestudios': ['nivelestudios', 'niveldeestudios', 'niveldeestudiosalcanzado', 'estudios'],
      'profesionocupacion': ['profesionocupacion', 'profesionuocupacion', 'profesion', 'ocupacion'],
      'tiempoasistenciaiglesia': ['tiempoasistenciaiglesia', 'desdecuandoasisteaestaiglesia', 'tiempoasistencia', 'tiempoenlaiglesia'],
      // La columna "¿Ha hecho la oración de fe?" normalizada queda como "hahecholaoraciondefe"
      'oracionfe': ['oracionfe', 'hahecholaoraciondefe', 'hahahecholaoraciondefe', 'oraciondefe', 'fe'],
      'esbautizado': ['esbautizado', 'habautizado', 'esbautizado', 'bautizado', 'bautismo'],
      'bautismoano': ['bautismoano', 'anoaproximadodesubautismo', 'anodebautismo', 'anobautismo'],
      'bautismoiglesia': ['bautismoiglesia', 'iglesiabautismo', 'iglesiadebautismo', 'iglesiabautizado', 'iglesiadondefuebautizad', 'iglesiadebautizado'],
      'bautismoministro': ['bautismoministro', 'ministrobautismo', 'ministroquelobautizo', 'ministrodebautismo'],
      'estudioscursos': ['estudioscursos', 'estudiosrealizados', 'estudiosrealizadosenlacongregacion', 'cursos', 'estudiosbiblicos'],
      'tipopersonalidad': ['tipopersonalidad', 'comoseconsideraprincipalmente', 'personalidad', 'tipodepersonalidad'],
      'habilidades': ['habilidades', 'seleccionealmenos1habilidad', 'habilidad'],
      'donesespirituales': ['donesespirituales', 'reconocealgundon', 'dones', 'don'],
      'ministeriosparticipacion': ['ministeriosparticipacion', 'perteneceactualmenteaalgunministerio', 'ministerioactual', 'ministeriosactuales', 'acual'],
      'ministeriosdeseados': ['ministeriosdeseados', 'sinestaenalgunministerioydeseaservirenqueministeriolegustariaestar', 'ministeriodeseado', 'ministeriopreferido'],
      'fechaencuesta': ['fechaencuesta', 'marcatemporal', 'fecharegistro', 'fecha'],
    };

    // Función para encontrar el índice de un header considerando aliases
    const findHeaderIndex = (standardName: string): number | undefined => {
      const aliases = headerAliases[standardName];
      if (!aliases) return headerMap.get(standardName);
      
      for (const alias of aliases) {
        const index = headerMap.get(alias);
        if (index !== undefined) return index;
      }
      return undefined;
    };

    // Validar que exista al menos un header de número de documento
    const numeroDocumentoIndex = findHeaderIndex('numerodocumento');
    if (numeroDocumentoIndex === undefined) {
      throw new Error('No se encontró la columna "Número de documento". Headers disponibles: ' + Array.from(headerMap.keys()).join(', '));
    }

    // Función helper para obtener valor usando aliases
    const getValueByAlias = (row: any[], standardName: string): string | undefined => {
      const index = findHeaderIndex(standardName);
      if (index === undefined) return undefined;
      
      const value = row[index];
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      return String(value).trim();
    };

    // Procesar filas de datos
    const datosComplementarios: BulkDatosComplementariosItemRaw[] = [];
    let skippedCount = 0;
    const skippedNames: string[] = [];
    
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0 || !row.some(cell => cell !== null && cell !== undefined && cell !== '')) {
        continue; // Saltar filas vacías
      }

      try {
        const numeroDocumento = row[numeroDocumentoIndex];
        if (!numeroDocumento || String(numeroDocumento).trim() === '') {
          continue; // Saltar filas sin número de documento
        }

        const numeroDocStr = String(numeroDocumento).trim();
        
        // Validar que sea un número de documento válido (solo números, al menos 5 dígitos)
        // Si contiene letras o es muy corto, probablemente sea un nombre
        const soloNumeros = /^\d+$/.test(numeroDocStr);
        if (!soloNumeros || numeroDocStr.length < 5) {
          skippedCount++;
          // Guardar los primeros 5 nombres omitidos para el mensaje
          if (skippedNames.length < 5 && numeroDocStr.length > 3) {
            skippedNames.push(numeroDocStr.substring(0, 30));
          }
          continue; // Omitir filas donde el "número de documento" parece ser un nombre
        }

        // Debug: mostrar valores crudos del Excel
        const rawOracionFe = getValueByAlias(row, 'oracionfe');
        const rawEsBautizado = getValueByAlias(row, 'esbautizado');
        
        if (i <= 5) {
          console.log(`Fila ${i} - Documento: ${numeroDocStr}`);
          console.log(`  Raw oracionFe: "${rawOracionFe}"`);
          console.log(`  Raw esBautizado: "${rawEsBautizado}"`);
        }

        const item: BulkDatosComplementariosItemRaw = {
          numeroDocumento: numeroDocStr,
          nivelEstudios: getValueByAlias(row, 'nivelestudios'),
          profesionOcupacion: getValueByAlias(row, 'profesionocupacion'),
          tiempoAsistenciaIglesia: getValueByAlias(row, 'tiempoasistenciaiglesia'),
          oracionFe: rawOracionFe,
          esBautizado: rawEsBautizado,
          bautismoAno: this.parseOptionalNumber(getValueByAlias(row, 'bautismoano')),
          bautismoIglesia: getValueByAlias(row, 'bautismoiglesia'),
          bautismoMinistro: getValueByAlias(row, 'bautismoministro'),
          estudiosCursos: getValueByAlias(row, 'estudioscursos'),
          tipoPersonalidad: getValueByAlias(row, 'tipopersonalidad'),
          habilidades: getValueByAlias(row, 'habilidades'),
          donesEspirituales: getValueByAlias(row, 'donesespirituales'),
          ministeriosParticipacion: getValueByAlias(row, 'ministeriosparticipacion'),
          ministeriosDeseados: getValueByAlias(row, 'ministeriosdeseados'),
          fechaEncuesta: getValueByAlias(row, 'fechaencuesta'),
        };

        datosComplementarios.push(item);
      } catch (error) {
        console.warn(`Error procesando fila ${i + 1}:`, error);
        continue; // Saltar filas con errores
      }
    }

    if (datosComplementarios.length === 0) {
      let errorMsg = 'No se encontraron datos válidos en el archivo';
      if (skippedCount > 0) {
        errorMsg += `. Se omitieron ${skippedCount} filas que no tenían número de documento válido`;
        if (skippedNames.length > 0) {
          errorMsg += ` (ej: ${skippedNames.join(', ')})`;
        }
      }
      throw new Error(errorMsg);
    }

    // Agregar metadata sobre filas omitidas al array (propiedad no enumerable)
    Object.defineProperty(datosComplementarios, 'skippedInfo', {
      value: {
        count: skippedCount,
        names: skippedNames
      },
      enumerable: false,
      writable: false
    });

    return datosComplementarios;
  }

  private parseOptionalNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const numValue = Number(value);
    return isNaN(numValue) ? undefined : numValue;
  }

  private formatFechaEncuesta(value: string | undefined): string {
    if (!value) {
      return new Date().toISOString().split('T')[0]; // Hoy en formato YYYY-MM-DD
    }

    // Si ya está en formato ISO (YYYY-MM-DD), devolverlo tal cual
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.split('T')[0];
    }

    // Si es un número (formato Excel de fecha), convertirlo
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue > 40000) {
      // Excel dates start from 1900-01-01
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
      const jsDate = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
      return jsDate.toISOString().split('T')[0];
    }

    // Formato DD/MM/YYYY HH:mm:ss o DD/MM/YYYY
    const stringValue = String(value).trim();
    
    // Formato con fecha y hora: DD/MM/YYYY HH:mm:ss
    const dateTimeWithSecondsRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/;
    const matchWithSeconds = stringValue.match(dateTimeWithSecondsRegex);
    if (matchWithSeconds) {
      const [, day, month, year] = matchWithSeconds;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // Formato sin segundos: DD/MM/YYYY HH:mm
    const dateTimeWithoutSecondsRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})$/;
    const matchWithoutSeconds = stringValue.match(dateTimeWithoutSecondsRegex);
    if (matchWithoutSeconds) {
      const [, day, month, year] = matchWithoutSeconds;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // Solo fecha: DD/MM/YYYY
    const dateOnlyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const matchDateOnly = stringValue.match(dateOnlyRegex);
    if (matchDateOnly) {
      const [, day, month, year] = matchDateOnly;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // Si no coincide con ningún formato, intentar crear una fecha
    const fallbackDate = new Date(stringValue);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate.toISOString().split('T')[0];
    }

    // Si todo falla, devolver la fecha actual
    console.warn(`No se pudo parsear la fecha: ${value}, usando fecha actual`);
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Valida el archivo antes de procesarlo
   */
  validateFile(file: File): void {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande. Tamaño máximo: 10MB');
    }

    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      throw new Error('Tipo de archivo no válido. Solo se permiten archivos .xlsx y .xls');
    }
  }
}

export const bulkUploadService = new BulkUploadService();